import { GAME_LEVELS } from "../constants/gameLevels";

const PAQUET_MAX = 5;

export function getWordPaquetNumber(word) {
  const p = word?.paquet;
  const n =
    typeof p === "number"
      ? p
      : typeof p === "string" && p.trim() !== ""
        ? parseInt(p, 10)
        : NaN;
  if (Number.isFinite(n) && n >= 1 && n <= PAQUET_MAX) return n;
  return null;
}

function uniqueMotList(values = []) {
  const seen = new Set();
  const result = [];
  for (const value of values) {
    const mot = String(value || "")
      .trim()
      .toLowerCase();
    if (!mot || seen.has(mot)) continue;
    seen.add(mot);
    result.push(mot);
  }
  return result;
}

function normalizeNoyauActifList(config = {}) {
  if (Array.isArray(config.noyauActif) && config.noyauActif.length) {
    return uniqueMotList(config.noyauActif);
  }
  return uniqueMotList([
    ...(config.noyauFavoris || []),
    ...(config.noyauPrioritaires || []),
  ]);
}

function sortAlphaMots(mots) {
  return [...mots].sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
}

export function buildPaquetsFromWords(words) {
  const maxNum = words.reduce((max, w) => {
    const p = getWordPaquetNumber(w);
    return p && p > max ? p : max;
  }, 0);
  const paquets = [];
  for (let n = 1; n <= maxNum; n += 1) {
    paquets.push(
      sortAlphaMots(words.filter((w) => getWordPaquetNumber(w) === n).map((w) => w.mot))
    );
  }
  return paquets;
}

export function isNoyauGenerated(config) {
  if (!config) return false;
  if (config.noyauGenerated === true) return true;
  return normalizeNoyauActifList(config).length > 0;
}

export function getDisplayNoyauMots(config, words) {
  const fromConfig = normalizeNoyauActifList(config);
  if (fromConfig.length) {
    return new Set(fromConfig);
  }
  return new Set(
    (words || []).filter((word) => word?.noyauActif === true).map((word) => word.mot)
  );
}

/** Pool de mots jouables pour un niveau (1–6), aligné sur la logique serveur. */
export function buildGameWordPool(level, words, config) {
  const lvl = Number(level);
  if (!Number.isFinite(lvl) || lvl < 1 || lvl > GAME_LEVELS.length) return [];

  const validWords = (Array.isArray(words) ? words : []).filter((w) => w?.mot);

  if (lvl === GAME_LEVELS.length) {
    return validWords.map((w) => w.mot);
  }

  const pool = new Set(getDisplayNoyauMots(config, validWords));
  const paquets = buildPaquetsFromWords(validWords);
  const packCount = lvl - 1;
  for (let i = 0; i < packCount && i < paquets.length; i += 1) {
    for (const mot of paquets[i] || []) {
      pool.add(mot);
    }
  }
  return [...pool];
}

export function filterWordsForGameLevel(level, words, config) {
  const poolMots = new Set(buildGameWordPool(level, words, config));
  return (Array.isArray(words) ? words : []).filter((w) => w?.mot && poolMots.has(w.mot));
}

/** Mots ajoutés dans ce niveau par rapport au niveau précédent. */
export function buildIncrementalPoolMots(level, words, config) {
  const lvl = Number(level);
  const validWords = (Array.isArray(words) ? words : []).filter((w) => w?.mot);

  if (lvl === 1) {
    return buildGameWordPool(1, words, config);
  }

  if (lvl >= 2 && lvl <= 5) {
    const paquetNum = lvl - 1;
    return validWords
      .filter((w) => getWordPaquetNumber(w) === paquetNum)
      .map((w) => w.mot);
  }

  if (lvl === 6) {
    const level5Pool = new Set(buildGameWordPool(5, words, config));
    return validWords.filter((w) => !level5Pool.has(w.mot)).map((w) => w.mot);
  }

  return [];
}

export function getIncrementalPoolLabel(level) {
  const lvl = Number(level);
  if (lvl === 1) return "Noyau actif uniquement";
  if (lvl >= 2 && lvl <= 5) return `Paquet ${lvl - 1} uniquement`;
  if (lvl === 6) return "Mots hors pool du niveau 5";
  return "Paquet en plus uniquement";
}

export function getMatrixPoolScopeOptions(level) {
  return [
    { value: "cumulative", label: "Pool cumulatif" },
    { value: "incremental", label: getIncrementalPoolLabel(level) },
  ];
}

export function filterWordsForGameLevelScope(level, words, config, scope = "cumulative") {
  if (scope === "incremental") {
    const poolMots = new Set(buildIncrementalPoolMots(level, words, config));
    return (Array.isArray(words) ? words : []).filter((w) => w?.mot && poolMots.has(w.mot));
  }
  return filterWordsForGameLevel(level, words, config);
}

export const MATRIX_POOL_SCOPE_VALUES = new Set(["cumulative", "incremental"]);

export function readMatrixPoolScope() {
  try {
    const raw = localStorage.getItem("vocabularium_matrix_pool_scope");
    return MATRIX_POOL_SCOPE_VALUES.has(raw) ? raw : "cumulative";
  } catch {
    return "cumulative";
  }
}

export function writeMatrixPoolScope(value) {
  if (!MATRIX_POOL_SCOPE_VALUES.has(value)) return;
  try {
    localStorage.setItem("vocabularium_matrix_pool_scope", value);
  } catch {
    // quota / mode privé : on ignore
  }
}

export function getMatrixPoolScopeRecapLabel(level, scope) {
  if (scope !== "incremental") return null;
  return getIncrementalPoolLabel(level).toLowerCase();
}

export const MATRIX_POOL_FILTER_OPTIONS = [
  { value: "all", label: "Vision générale — tous les mots" },
  ...GAME_LEVELS.map((level) => ({
    value: String(level.id),
    label: `Niveau ${level.id} — ${level.name}`,
  })),
];

export function readMatrixPoolFilter() {
  try {
    const raw = localStorage.getItem("vocabularium_matrix_pool_filter");
    if (raw === "all") return "all";
    if (GAME_LEVELS.some((level) => String(level.id) === raw)) return raw;
    return "all";
  } catch {
    return "all";
  }
}

export function writeMatrixPoolFilter(value) {
  try {
    localStorage.setItem("vocabularium_matrix_pool_filter", value);
  } catch {
    // quota / mode privé : on ignore
  }
}

export function getMatrixPoolFilterLabel(value) {
  if (value === "all") return "Vision générale";
  const level = GAME_LEVELS.find((entry) => String(entry.id) === value);
  return level ? `Niveau ${level.id} — ${level.name}` : "Vision générale";
}
